import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, HardHat, Printer, Search, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ProPortalHeader from '@/components/ProPortalHeader';
import { HelpToc } from '@/components/help/HelpToc';
import { ManualChapter } from '@/components/help/ManualChapter';
import { RulesChapter } from '@/components/help/RulesChapter';
import { FaqChapter } from '@/components/help/FaqChapter';
import { tileMatches } from '@/components/help/manualTheme';
import { INSTALLER_GUIDE } from '@/data/manual/installerGuide';
import useVisibleManual from '@/hooks/useVisibleManual';

const TABS = [
  { id: 'staff', label: 'Staff manual', icon: BookOpen },
  { id: 'installer', label: 'Installer & trainee guide', icon: HardHat },
  { id: 'rules', label: 'Rules & automations', icon: Scale },
];

export default function StaffHelp() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'staff';
  const [query, setQuery] = useState('');
  const [full, setFull] = useState(params.get('mode') !== 'quick');
  const [printing, setPrinting] = useState(false);
  const { sections, groups, layoutApplied } = useVisibleManual(query);

  const installerSections = useMemo(
    () => INSTALLER_GUIDE.map((s) => ({ section: s, tiles: s.tiles.filter((t) => tileMatches(t, query)) })).filter((x) => x.tiles.length),
    [query],
  );

  useEffect(() => {
    const after = () => setPrinting(false);
    window.addEventListener('afterprint', after);
    return () => window.removeEventListener('afterprint', after);
  }, []);

  const print = () => { setPrinting(true); setTimeout(() => window.print(), 150); };
  const showFull = full || printing;
  const active = tab === 'installer' ? installerSections : sections;
  const tocSections = active.map((x) => ({ ...x.section, count: x.tiles.length }));
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="staff-help-page">
      <div className="no-print"><ProPortalHeader activeSection="staff" /></div>

      <div className="bg-white border-b border-gray-200 no-print sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex flex-wrap items-center gap-2 sm:gap-3">
          <button type="button" onClick={() => navigate('/staff')} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#27292E]" data-testid="help-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <h1 className="text-base sm:text-lg font-bold text-[#27292E] mr-auto">Help & User Manual</h1>
          <div className="relative w-full sm:w-64 order-last sm:order-none">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search the manual…" className="pl-8 h-9" data-testid="help-search" />
          </div>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs font-semibold" data-testid="help-mode">
            <button type="button" onClick={() => setFull(false)} data-testid="help-mode-quick" className={`px-3 py-1.5 ${!full ? 'bg-[#27292E] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Quick reference</button>
            <button type="button" onClick={() => setFull(true)} data-testid="help-mode-full" className={`px-3 py-1.5 ${full ? 'bg-[#27292E] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Full detail</button>
          </div>
          <Button size="sm" onClick={print} className="bg-[#2F5E96] hover:bg-[#264d7a] text-white" data-testid="help-print">
            <Printer className="w-4 h-4 mr-1.5" /> Print / Save as PDF
          </Button>
        </div>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 pb-2 flex gap-1 pill-row">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setParams({ tab: t.id })} data-testid={`help-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${tab === t.id ? 'bg-[#7A0B7A] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="print-only hidden px-2 pb-4 border-b border-gray-300 mb-4">
        <div className="text-3xl font-bold text-[#27292E]">Ox Resin Installer Portal</div>
        <div className="text-lg text-gray-700">{tab === 'installer' ? 'Installer & trainee guide' : tab === 'rules' ? 'Rules & automations' : 'Staff user manual'} · {today}</div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-5 grid gap-5 lg:grid-cols-[240px_1fr]">
        <div>{tab !== 'rules' && <HelpToc sections={tocSections} extra={tab === 'staff' ? [{ id: 'rules', title: 'Rules & automations' }, { id: 'faq', title: 'Frequently asked' }] : []} />}</div>
        <div className="space-y-5 min-w-0">
          {tab === 'staff' && (
            <p className="text-sm text-gray-600" data-testid="help-role-note">
              This manual only shows the sections and tiles you have access to{layoutApplied ? ', in the same order and sections as your Staff Dashboard' : ''}. Use <strong>Full detail</strong> for step-by-step walkthroughs; <strong>Quick reference</strong> for the short version.
            </p>
          )}
          {tab !== 'rules' && active.map((x, i) => <ManualChapter key={x.section.id} section={x.section} tiles={x.tiles} full={showFull} index={tab === 'staff' ? i + 1 : null} />)}
          {tab !== 'rules' && active.length === 0 && <div className="text-sm text-gray-500 italic" data-testid="help-empty">Nothing matches "{query}".</div>}
          {tab !== 'installer' && <RulesChapter query={query} groups={tab === 'rules' ? null : groups} />}
          {tab === 'staff' && <FaqChapter query={query} />}
        </div>
      </div>
    </div>
  );
}
